import { useCallback, useRef } from 'react';
import { uazapiService } from '../../../services/uazapiService';
import { ChatMessage, CHAT_DELETED_LABEL, CHAT_DELETING_LABEL, type SendQuotedRef } from './types';
import { createModuleLogger } from '../../../utils/logger';
import { notify } from '../../../utils/toast';
import {
    createChatSendQueue,
    type EnqueueChatSend,
    messageTimestampMs,
    persistEditedMessageInDb,
    persistMessageInDb,
    resolveMutationTargets,
} from '../lib/chatMessageMutations';

import { useEnsureConversation } from './useEnsureConversation';
import { useChatTextActions } from './useChatTextActions';
import { useChatMediaActions } from './useChatMediaActions';
import { useChatSpecialActions } from './useChatSpecialActions';
import { useChatSignature } from './useChatSignature';

const log = createModuleLogger('[ChatActions]');

interface UseChatActionsProps {
    instanceName: string | undefined;
    activeConversationId: string | null;
    conversationDbId: string | null;
    chatPlatform?: 'whatsapp' | 'instagram';
    messagingConnectionId?: string | null;
    officialApi?: boolean;
    userId: string | null;
    currentOrganizationId?: string;
    addOptimisticMessage: (msg: ChatMessage) => void;
    replaceOptimisticMessage: (oldId: string, newMsg: ChatMessage) => void;
    removeOptimisticMessage: (msgId: string) => void;
    patchLocalMessages: (ids: string[], patch: Partial<ChatMessage>, contentHint?: string) => void;
    clearLocalOverride: (ids: string[], contentHint?: string) => void;
    getLocalMessage?: (messageId: string) => ChatMessage | undefined;
    onSendFailure?: (
        error: unknown,
        text: string,
        quotedMessage?: SendQuotedRef
    ) => void;
    onSendSucceeded?: () => void;
    setConversationDbId: (id: string) => void;
}

export function useChatActions({
    instanceName,
    activeConversationId,
    conversationDbId,
    chatPlatform = 'whatsapp',
    messagingConnectionId,
    officialApi = false,
    userId,
    currentOrganizationId,
    addOptimisticMessage,
    replaceOptimisticMessage,
    removeOptimisticMessage,
    patchLocalMessages,
    clearLocalOverride,
    getLocalMessage,
    setConversationDbId,
    onSendFailure,
    onSendSucceeded,
}: UseChatActionsProps) {
    const { ensureConversationExists } = useEnsureConversation(
        instanceName,
        conversationDbId,
        activeConversationId,
        userId,
        currentOrganizationId,
        setConversationDbId,
        officialApi || chatPlatform === 'instagram' ? messagingConnectionId : null,
    );

    const queueRef = useRef<ReturnType<typeof createChatSendQueue>>();
    if (!queueRef.current) queueRef.current = createChatSendQueue();
    const sendKey = `${currentOrganizationId}:${messagingConnectionId || instanceName}:${activeConversationId}`;
    const enqueueSend: EnqueueChatSend = useCallback(
        (send) => queueRef.current!(sendKey, send), [sendKey],
    );

    const { applySignature } = useChatSignature();

    const sharedSend = {
        enqueueSend,
        instanceName,
        activeConversationId,
        addOptimisticMessage,
        replaceOptimisticMessage,
        removeOptimisticMessage,
        ensureConversationExists,
        currentOrganizationId,
    };

    const { sendMessage } = useChatTextActions({
        ...sharedSend,
        chatPlatform,
        messagingConnectionId,
        officialApi,
        applySignature,
        onSendFailure,
        onSendSucceeded,
    });
    const { sendMedia } = useChatMediaActions({
        ...sharedSend,
        applySignature,
        chatPlatform,
        messagingConnectionId,
        officialApi,
    });
    const { removeOptimisticMessage: _, ...specialSend } = sharedSend;
    const { sendContactCard, sendLocationMessage, sendPixMessage } =
        useChatSpecialActions(specialSend);

    const deleteMessage = useCallback(async (messageId: string) => {
        if (!instanceName || !activeConversationId) return;

        const local = getLocalMessage?.(messageId);
        const previousText = local?.text;
        const previousType = local?.type;
        const previousMedia = local?.media;

        patchLocalMessages([messageId], {
            text: CHAT_DELETING_LABEL,
            type: 'text',
            media: undefined,
        });

        try {
            const { dbId, resolvedId, ids, number } = await resolveMutationTargets(
                conversationDbId,
                activeConversationId,
                currentOrganizationId,
                messageId,
                previousText,
                messageTimestampMs(local)
            );

            patchLocalMessages(ids, {
                text: CHAT_DELETED_LABEL,
                type: 'text',
                media: undefined,
                is_edited: false,
            });

            if (dbId) {
                await persistMessageInDb(dbId, currentOrganizationId, ids, {
                    content: CHAT_DELETED_LABEL,
                    message_type: 'text',
                    media_url: null,
                    is_edited: false,
                });
            }

            await uazapiService.deleteMessage(instanceName, number, resolvedId);
            log.log('🗑️ Mensagem excluída no WhatsApp e no sistema');
        } catch (err) {
            log.error('Erro ao excluir mensagem:', err);
            patchLocalMessages([messageId], {
                text: previousText,
                type: previousType,
                media: previousMedia,
                is_edited: local?.is_edited,
            });
            notify.error(err instanceof Error ? err.message : 'Erro ao excluir mensagem');
        }
    }, [
        instanceName,
        activeConversationId,
        conversationDbId,
        currentOrganizationId,
        patchLocalMessages,
        getLocalMessage,
    ]);

    const editMessage = useCallback((messageId: string, newText: string, originalText?: string) => {
        if (!instanceName || !activeConversationId) {
            notify.error('Conexão ou conversa indisponível.');
            return;
        }

        const trimmed = newText.trim();
        if (!trimmed) {
            notify.error('Digite o novo texto da mensagem.');
            return;
        }

        const signedText = applySignature(trimmed);
        const editPatch = { text: signedText, is_edited: true as const };
        patchLocalMessages([messageId], editPatch, originalText);

        void (async () => {
            try {
                const { dbId, resolvedId, ids, number } = await resolveMutationTargets(
                    conversationDbId,
                    activeConversationId,
                    currentOrganizationId,
                    messageId,
                    originalText,
                    messageTimestampMs(getLocalMessage?.(messageId))
                );

                await uazapiService.editMessage(instanceName, number, resolvedId, signedText);
                patchLocalMessages(ids, editPatch, originalText);

                if (dbId) {
                    const persisted = await persistEditedMessageInDb(
                        dbId,
                        currentOrganizationId,
                        ids,
                        originalText,
                        signedText
                    );
                    if (persisted) clearLocalOverride(ids, originalText);
                    else log.warn('Edição no WhatsApp OK, mas linha não encontrada no banco — mantendo override local');
                }

                log.log('✏️ Mensagem editada (com sucesso)');
            } catch (error: unknown) {
                if (originalText) {
                    patchLocalMessages([messageId], { text: originalText, is_edited: false }, originalText);
                    clearLocalOverride([messageId], originalText);
                }
                log.error('❌ Erro ao editar mensagem:', error);
                const msg = error instanceof Error ? error.message : 'Erro ao editar mensagem';
                notify.error(
                    /404/.test(msg)
                        ? 'Não foi possível editar. Verifique se a mensagem foi enviada há menos de 15 minutos.'
                        : msg
                );
            }
        })();
    }, [
        instanceName,
        activeConversationId,
        conversationDbId,
        currentOrganizationId,
        applySignature,
        patchLocalMessages,
        clearLocalOverride,
        getLocalMessage,
    ]);

    return {
        sendMessage,
        sendMedia,
        sendContactCard,
        sendLocationMessage,
        sendPixMessage,
        deleteMessage,
        editMessage,
    };
}
